import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ListAltIcon from '@mui/icons-material/ListAlt'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import EmptyState from '../../components/inspector/shared/EmptyState'
import SearchSelect from '../../components/shared/inputs/SearchSelect'
import { useDataFetching } from '../../hooks/useDataFetching'
import { useListPageState } from '../../hooks/useListPageState'
import { getSystemIcon } from '../../components/SystemIcons'
import { highlightMatch } from '../../utils/highlightMatch'
import {
  pwxMastersApi,
  PwxMasterListResponse,
  PwxMasterSummary,
  PwxMasterValue,
} from '../../services/admin/pwxMastersApi'

const SYSTEM_LABELS: Record<string, string> = {
  lightspeed: 'Lightspeed',
  xero: 'Xero',
  skimmer: 'Skimmer',
  pooltrackr: 'Pooltrackr',
}

const formatSystem = (s: string): string => SYSTEM_LABELS[s.toLowerCase()] ?? s

const masterLabel = (s: PwxMasterSummary): string => s.name

const PwxMasters: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: mastersResponse, loading: mastersLoading, error: mastersError } =
    useDataFetching<PwxMasterListResponse>({
      fetchFn: () => pwxMastersApi.list(),
      dependencies: [],
    })

  const masters = useMemo(() => {
    const items = mastersResponse?.items ?? []
    return [...items].sort((a, b) =>
      masterLabel(a).localeCompare(masterLabel(b)),
    )
  }, [mastersResponse])

  // The selected master is derived from the URL (`?master=<code>&country=<cc>`)
  // so the link is copy/paste-shareable. `code` alone isn't unique - the same
  // code exists per country - so country is part of the key (omitted for the
  // global, country-less list).
  const masterParam = searchParams.get('master')
  const countryParam = searchParams.get('country')

  const selected = useMemo(
    () =>
      masters.find(
        (s) =>
          s.code === masterParam &&
          (s.country_code ?? null) === (countryParam || null),
      ) ?? null,
    [masters, masterParam, countryParam],
  )

  const selectedId = selected?.id ?? null

  const selectMaster = useCallback(
    (m: PwxMasterSummary | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (m) {
            next.set('master', m.code)
            if (m.country_code) next.set('country', m.country_code)
            else next.delete('country')
          } else {
            next.delete('master')
            next.delete('country')
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  // Default page (no master in the URL): nothing is selected - we focus the
  // picker so the user can start typing/choosing straight away, and the table
  // area shows a "pick a list" placeholder. A stale/invalid param stays
  // unselected too (no silent fallback).
  const masterInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!mastersLoading && masters.length > 0 && !masterParam) {
      masterInputRef.current?.focus()
    }
  }, [mastersLoading, masters.length, masterParam])

  const fetchValues = useCallback(
    () =>
      selectedId === null
        ? Promise.resolve({ items: [] as PwxMasterValue[] })
        : pwxMastersApi.values(selectedId).then((r) => ({ items: r.items })),
    [selectedId],
  )

  const { data: valuesData, loading: valuesLoading, error: valuesError } =
    useDataFetching<{ items: PwxMasterValue[] }>({
      fetchFn: fetchValues,
      dependencies: [selectedId],
    })

  // Sort/page is client-side only here; keep it out of the URL so this hook
  // doesn't fight our `?master=&country=` params (its syncToURL rebuilds the
  // query from its own state and would otherwise strip them, causing a loop).
  const { state, updateField } = useListPageState({
    syncWithURL: false,
    defaultSortField: 'sort_order',
    defaultPerPage: 50,
  })

  const sortedValues = useMemo(() => {
    const rows = valuesData?.items ?? []
    if (!state.sortField) return rows
    const direction = state.sortDirection === 'desc' ? -1 : 1
    return [...rows].sort((a, b) => {
      const aVal = (a as any)[state.sortField] ?? ''
      const bVal = (b as any)[state.sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction
      }
      return String(aVal).localeCompare(String(bVal)) * direction
    })
  }, [valuesData, state.sortField, state.sortDirection])

  const valueColumns: TableColumn<PwxMasterValue>[] = [
    {
      key: 'sort_order',
      label: 'Order',
      sortable: true,
      width: 70,
      align: 'right',
    },
    {
      key: 'value',
      label: 'Value',
      sortable: true,
      render: (row) => (
        <Typography variant="body2" fontWeight={500}>
          {row.value}
        </Typography>
      ),
    },
    {
      key: 'display_name',
      label: 'Display name',
      render: (row) => row.display_name || '-',
    },
    {
      key: 'attributes',
      label: 'Attributes',
      render: (row) => {
        if (!row.attributes) return '-'
        const json = JSON.stringify(row.attributes)
        const preview = json.length > 60 ? `${json.slice(0, 57)}…` : json
        return (
          <Tooltip
            title={<pre style={{ margin: 0 }}>{JSON.stringify(row.attributes, null, 2)}</pre>}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
            >
              {preview}
            </Typography>
          </Tooltip>
        )
      },
    },
    {
      key: 'is_active',
      label: 'Active',
      sortable: true,
      width: 80,
      align: 'center',
      render: (row) => (row.is_active ? 'Yes' : 'No'),
    },
  ]

  return (
    <>
      <AdminPageHeader
        pageKey="pwxMasters"
        descriptionOverride="Poolwerx master lists consumed by exception checks to verify each franchise partner has the right setup across systems. View only - managed by the development team."
      />
      {mastersError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {mastersError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
        >
          <SearchSelect<PwxMasterSummary>
            sx={{ minWidth: 480 }}
            inputRef={masterInputRef}
            options={masters}
            value={selected}
            disabled={mastersLoading || masters.length === 0}
            placeholder="Search master lists..."
            getOptionLabel={(m) => masterLabel(m)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(m: PwxMasterSummary | null) => selectMaster(m)}
            renderOption={(props, option, { inputValue }) => (
              <li {...props} key={(option as PwxMasterSummary).id}>
                <span>
                  {highlightMatch(
                    masterLabel(option as PwxMasterSummary),
                    inputValue,
                  )}
                </span>
              </li>
            )}
          />
          {selected && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              {selected.country_code && (
                <Chip size="small" label={selected.country_code} color="primary" />
              )}
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Used by:
                </Typography>
                {(selected.used_by ?? []).length > 0 ? (
                  selected.used_by!.map((sys) => (
                    <Tooltip key={sys} title={formatSystem(sys)}>
                      <Box sx={{ display: 'inline-flex' }}>
                        {getSystemIcon(sys, { size: 18 }) ?? (
                          <Chip size="small" label={sys} />
                        )}
                      </Box>
                    </Tooltip>
                  ))
                ) : (
                  <Typography variant="caption">All systems</Typography>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {valuesData?.items.length ?? 0} values
              </Typography>
            </Stack>
          )}
        </Stack>
        {selected?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {selected.description}
          </Typography>
        )}
      </Paper>

      {selected ? (
        <BaseDataTable<PwxMasterValue>
          variant="enhanced"
          columns={valueColumns}
          data={sortedValues}
          loading={valuesLoading}
          error={valuesError}
          emptyMessage="No values defined for this master."
          sortField={state.sortField}
          sortDirection={state.sortDirection}
          onSort={(field, direction) => {
            updateField('sortField', field)
            updateField('sortDirection', direction)
          }}
          showCount
          countLabel="values"
          getRowKey={(row) => row.id}
        />
      ) : (
        <EmptyState
          type="custom"
          icon={<ListAltIcon />}
          title="Select a master list"
          description="Choose a list from the dropdown above to view its values."
        />
      )}
    </>
  )
}

export default PwxMasters
